import { skyGuardianAnatomy } from '../../../packages/domain/src/83-sky-anatomy.mjs';
import { skyGuardianPose } from '../../../packages/domain/src/76-sky-campaign.mjs';

const smooth = (t) => t * t * (3 - 2 * t);
// A strong, short power stroke, slower recovery, then a held glide. The curve
// has zero velocity at its joins, so no hinge snaps at low mobile frame rates.
function wingStroke(time) {
  const phase = (((time / 2.6) % 1) + 1) % 1;
  if (phase < 0.22) return 0.27 - 0.69 * smooth(phase / 0.22);
  if (phase < 0.72) return -0.42 + 0.69 * smooth((phase - 0.22) / 0.5);
  return 0.27;
}

/** Apply presentation hinges in radians from domain age in seconds. Rest
 * transforms are restored each call; pause, rewind and reduced motion cannot
 * accumulate drift. Authoritative movement and collisions remain in domain76. */
export function animateSkyArticulation(group, item, reduced, charge = 0, recoil = 0) {
  const anatomy =
    item.kind === 'boss'
      ? (item.anatomy ??
        skyGuardianAnatomy({ ...item, profile: item.profile ?? group.userData.profile }))
      : null;
  if (anatomy) group.rotation.set(...anatomy.rootRotation);
  const time = (item.age ?? 0) + (item.animationOffset ?? 0);
  if (!group.userData.tailMorphs) {
    group.userData.tailMorphs = [];
    group.traverse((node) => {
      const index = node.morphTargetDictionary?.tail_sweep;
      if (index !== undefined) group.userData.tailMorphs.push({ node, index });
    });
  }
  for (const { node, index } of group.userData.tailMorphs) {
    node.morphTargetInfluences[index] = reduced ? 0 : (Math.sin(time * 1.2) + 1) * 0.3;
  }
  const profile = item.profile ?? group.userData.profile;
  const stone = profile === 'stoneOracle';
  const technology = group.userData.form === 'carrier' || group.userData.form === 'sentinel';
  const headPose =
    item.kind === 'boss' ? skyGuardianPose(profile, time, item.artVariant, item.design) : null;
  const trail = (segment) => {
    if (!headPose) return { x: 0, y: 0 };
    const prior = skyGuardianPose(
      profile,
      Math.max(0, time - (segment + 1) * 0.14),
      item.artVariant,
      item.design,
    );
    const scale = group.scale.x || 1;
    return {
      x: 2.5 * Math.tanh((prior.x - headPose.x) / (scale * 2.5)),
      y: 1.5 * Math.tanh((prior.y - headPose.y) / (scale * 1.5)),
    };
  };
  for (const [index, part] of (group.userData.parts ?? []).entries()) {
    const node = part.node;
    node.rotation.copy(part.base);
    node.position.copy(part.position);
    const name = node.name;
    const jointName = name.replace(/[._]?\d+$/, '');
    const canonical = anatomy?.targets.some((target) => target.parent === jointName)
      ? anatomy.joints[jointName]
      : null;
    if (canonical) node.rotation.set(...canonical);
    let mouth;
    if (name.includes('jaw')) {
      let parent = node.parent;
      while (parent && parent !== group && !mouth) {
        mouth = anatomy?.targets.find(
          (target) => target.parent === parent.name.replace(/[._]?\d+$/, ''),
        );
        parent = parent.parent;
      }
      if (mouth) node.rotation.x += mouth.opening * 0.65;
    }
    if (reduced) continue;
    const side = name.includes('left') ? -1 : 1;
    const stride = Math.sin(time * 2.05);
    if (canonical || mouth) {
      // Hit-bearing joints already use the same pure transform as collision.
    } else if (name.includes('vertebra')) {
      const segment = Number(name.match(/vertebra_(\d+)/)?.[1] ?? index);
      const current = trail(segment),
        next = trail(segment + 1);
      const ripple =
        Math.sin(time * 2.6 - segment * 0.5) * (0.04 + segment * 0.012) * (1 - charge * 0.5);
      node.position.x += current.x + ripple;
      node.position.y += current.y;
      node.position.z += recoil * 0.12 * Math.exp(-segment * 0.2);
      node.rotation.y += Math.atan2(current.x - next.x, 1.05);
      node.rotation.x -= Math.atan2(current.y - next.y, 1.05);
    } else if (name.includes('craft_wing')) {
      const membrane = group.userData.craftForm === 'manta';
      const turn = Math.tanh((item.vx ?? 0) * 0.035 + (item.ax ?? 0) * 0.025);
      node.rotation.z +=
        side * (membrane ? wingStroke(time * 1.7 + side * 0.03) * 0.7 : -0.025) + turn * 0.12;
      node.rotation.x += side * turn * (membrane ? 0.08 : 0.18) - charge * 0.06;
      node.rotation.y += side * (membrane ? Math.sin(time * 4.1 - 0.4) * 0.09 : charge * 0.045);
    } else if (name.startsWith('turbine_')) {
      node.rotation.z += side * time * (4 + charge * 3);
      node.rotation.x += Math.sin(time * 1.1) * 0.045 - charge * 0.13;
      node.rotation.y += side * (0.04 + charge * 0.1);
    } else if (name.includes('wing')) {
      node.rotation.z +=
        side *
        (technology
          ? Math.sin(time * 1.3) * 0.035 + charge * 0.07
          : wingStroke(time) * (1 - charge * 0.5));
      node.rotation.y += side * (charge * 0.2 + recoil * 0.1);
    } else if (name.startsWith('head')) {
      node.rotation.y +=
        Math.sin(time * (stone ? 1.025 : 1.35) - 0.3) * (stone ? 0.08 : 0.055) * (1 - charge);
      node.rotation.x += Math.sin(time * 2.05 - 0.55) * 0.025 - charge * 0.07 + recoil * 0.12;
    } else if (name.includes('jaw')) {
      node.rotation.x += 0.04 + Math.sin(time * 2.4) * 0.025 + charge * 0.32 + recoil * 0.08;
    } else if (name.startsWith('torso')) {
      node.rotation.y += stride * 0.065 * (1 - charge);
      node.rotation.z += Math.sin(time * 2.05 - 0.4) * 0.045;
      node.rotation.x += charge * 0.055 - recoil * 0.08;
    } else if (name.startsWith('forearm_')) {
      node.rotation.x +=
        -0.18 -
        Math.max(0, side * Math.sin(time * 2.05 - 0.45)) * 0.2 -
        charge * 0.38 +
        recoil * 0.15;
    } else if (name.startsWith('leg_')) {
      node.rotation.x += stone
        ? side * stride * 0.2 * (1 - charge * 0.7)
        : 0.04 + Math.sin(time * 2.1 - 0.7 + side * 0.4) * 0.08;
      node.rotation.z += side * (stone ? 0.015 + Math.cos(time * 2.05) * 0.025 : 0.045);
    } else if (name.startsWith('tendril_')) {
      const segment = Number(name.match(/_(\d+)(?:\.\d+)?$/)?.[1] ?? 0);
      node.rotation.x += Math.sin(time * 1.4 - segment * 0.65) * 0.1 + charge * 0.18;
      node.rotation.z += side * (0.04 + Math.sin(time * 1.1 - segment * 0.7) * 0.08);
    } else if (name.includes('shell_')) {
      const designed = name.includes('shell_design');
      const segment = Number(name.match(/_(\d+)$/)?.[1] ?? 0);
      node.rotation.y +=
        side *
        (0.08 +
          charge * (designed ? 0.28 : 0.5) +
          recoil * 0.09 +
          Math.sin(time * 2.3 - segment * 0.3) * 0.04);
      if (designed) node.rotation.x += Math.sin(time * 1.8 - segment * 0.4) * 0.07;
    } else if (name.includes('arm_')) {
      node.rotation.z += side * (0.08 + Math.sin(time * 1.8) * 0.05 + charge * 0.15);
      node.rotation.x +=
        (stone ? -side * stride * 0.18 : Math.cos(time * 1.8) * 0.04) - charge * 0.3 + recoil * 0.2;
    } else if (name.includes('orbit_')) {
      const a = time * 0.45,
        x = part.position.x,
        y = part.position.y;
      node.position.set(
        x * Math.cos(a) - y * Math.sin(a),
        x * Math.sin(a) + y * Math.cos(a),
        part.position.z,
      );
      node.rotation.y += time * 0.25;
    }
    if (group.userData.artVariant === 'tidal-oracle' && /^(arm_|forearm_)/.test(name)) {
      // The source is rigid carved armor, so modest swings keep the authored
      // bearing surfaces engaged instead of exposing disconnected shell ends.
      node.rotation.x = part.base.x + (node.rotation.x - part.base.x) * 0.45;
      node.rotation.z = part.base.z + (node.rotation.z - part.base.z) * 0.55;
    }
    if (item.defeated) {
      const fall = smooth(Math.min(1, (item.deathAge ?? 0) / (item.deathDuration ?? 3.6)));
      if (name.includes('wing')) {
        node.rotation.z += side * fall * (technology ? 0.6 : 1.05);
        node.rotation.y -= side * fall * 0.4;
      } else if (name.startsWith('turbine_')) {
        node.rotation.z += side * 4 * (1 - Math.exp(-(item.deathAge ?? 0)));
        node.rotation.y += side * fall * 0.65;
      } else if (name.startsWith('head') || name.startsWith('torso')) {
        node.rotation.x += fall * (stone ? 0.65 : 0.45);
        node.position.y -= fall * (stone ? 0.6 : 0.2);
      } else if (name.includes('vertebra')) {
        const segment = Number(name.match(/vertebra_(\d+)/)?.[1] ?? index);
        node.position.y -= fall * Math.min(1.1, 0.16 + segment * 0.09);
        node.rotation.z += fall * Math.sin(segment * 0.4) * 0.25;
      } else if (name.startsWith('leg_')) {
        node.rotation.x += fall * (stone ? 0.9 : 0.5);
      } else if (name.includes('arm_')) {
        node.rotation.x += fall * 0.55;
        node.rotation.z += side * fall * 0.3;
      }
    }
  }
}
